import getQuotingCustomerContactsApi from '@/api/get/company/sales-flow/get-quoting-customer-contacts';
import getQuotingCustomersApi from '@/api/get/company/sales-flow/get-quoting-customers';
import Card from '@/components/card/card-component';
import { Col, Row } from '@/components/grid/grid-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import CreatableSelect from '@/components/react-hook-form-inputs/select-createable/creatable-search-component';
import { useLazyQuery } from '@/hooks/use-api';
import { SubheaderText } from '@/utils/global-fonts';
import { requiredValidationDropdownMessage } from '@/utils/validations';
import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { SUMMARY_CONTEXT } from './context-provider';
import QuickCreateCustomer from './quick-create-customer';
import QuickCreateCustomerContact from './quick-create-customer-contact';

const GreyLine = styled.div`
  display: flex;
  height: 1px;
  background-color: ${({ theme }) => theme.COLOR_GREY5};
`;

/**
 * Customer form for the Quote Summary page - Customer Tab.
 * @file CustomerForm.jsx
 * @returns {JSX.Element} The rendered Customer Form component.
 */
const QuoteCustomerForm = () => {
  const { quoteCustomer, setQuoteCustomer } = useContext(SUMMARY_CONTEXT);
  const [openQuickCreateCustomer, setOpenQuickCreateCustomer] = useState(false);
  const [openQuickCreateCustomerContact, setOpenQuickCreateCustomerContact] =
    useState(false);

  const {
    formState: { isLoading: formLoading },
    setValue,
    watch,
    clearErrors,
  } = useFormContext();

  const customerId = watch('customer.id');
  const contactId = watch('contact_person.id');

  const customersArray = quoteCustomer?.map((customer) => ({
    value: customer?.id,
    label: customer?.name,
  }));

  const {
    data: contactData,
    refetch: refetchContact,
    loading: loadingContacts,
  } = useLazyQuery(getQuotingCustomerContactsApi);

  const {
    data: customersData,
    refetch: refetchCustomers,
    loading: loadingCustomers,
  } = useLazyQuery(getQuotingCustomersApi);

  const contactPerson = contactData || [];

  useEffect(() => {
    if (customersData) {
      setQuoteCustomer(customersData);
    }
  }, [customersData, setQuoteCustomer]);

  const contactsArray = contactPerson?.map((person) => ({
    value: person?.id,
    label: person?.name,
  }));

  const contactDetails = contactPerson?.find(
    (contact) => contact?.id === contactId,
  );

  const clearContacts = (selectedOption) => {
    const selectedOptionValue = selectedOption?.value;
    if (!customerId) {
      setValue('contact_person.id', null);
      return;
    }
    if (
      selectedOptionValue !== customerId ||
      !selectedOptionValue ||
      selectedOptionValue === undefined ||
      selectedOptionValue === customerId
    ) {
      setValue('contact_person.id', null);
    }
  };

  useEffect(() => {
    const getContactsOnCustomerId = async () => {
      if (!customerId) {
        return;
      }
      await refetchContact(customerId);
    };

    getContactsOnCustomerId();
  }, [customerId, refetchContact]);

  const handleOnClose = async (id) => {
    if (id) {
      await refetchCustomers();

      setValue('customer.id', id, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });

      // clear error for customer.id field because it triggers validation error
      clearErrors('customer.id');
    }

    setOpenQuickCreateCustomer(false);
  };

  const handleOnCloseContact = async (id) => {
    if (id) {
      await refetchContact(customerId).then(() => {
        // clear error for contact_person.id field because it triggers validation error
        clearErrors('contact_person.id');
        setValue('contact_person.id', id, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
          defaultValue: id,
        });

        // clear error for contact_person.id field because it triggers validation error
        clearErrors('contact_person.id');
      });
    }

    setOpenQuickCreateCustomerContact(false);
  };

  return (
    <Card margin="8px 0px 8px 0px" borderRadius="4px">
      <SubheaderText>Customer</SubheaderText>
      <GreyLine />
      <Row $marginBottom="0 !important">
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          <CreatableSelect
            header="Customer"
            placeholder="Please select a customer"
            options={customersArray}
            name="customer.id"
            required={{
              required: requiredValidationDropdownMessage,
            }}
            formatCreateLabel="Add Customer"
            onCreateOption={() => {
              setOpenQuickCreateCustomer(true);
            }}
            onChange={clearContacts}
            isLoading={formLoading ?? loadingCustomers}
          />
        </Col>
        <Col>
          <CreatableSelect
            isDisabled={!customerId || loadingContacts}
            header="Contact"
            placeholder="Please select a contact"
            options={contactsArray}
            name="contact_person.id"
            required={{
              required: requiredValidationDropdownMessage,
            }}
            formatCreateLabel="Add Contact"
            onCreateOption={() => {
              setOpenQuickCreateCustomerContact(true);
            }}
            isLoading={loadingContacts}
          />
        </Col>
      </Row>
      {contactDetails !== undefined && (
        <Row $marginBottom="0 !important">
          <Col>
            <Input
              label="Email"
              placeholder="Email"
              name="email"
              disabled
              initialValue={contactDetails?.email}
            />
          </Col>
          <Col>
            <Input
              label="Phone Number"
              placeholder="Phone Number"
              name="number"
              disabled
              initialValue={contactDetails?.contact_number}
            />
          </Col>
        </Row>
      )}

      {openQuickCreateCustomer && (
        <QuickCreateCustomer
          open={openQuickCreateCustomer}
          onClose={handleOnClose}
        />
      )}

      {openQuickCreateCustomerContact && (
        <QuickCreateCustomerContact
          open={openQuickCreateCustomerContact}
          onClose={handleOnCloseContact}
          customerId={customerId}
        />
      )}
    </Card>
  );
};

export default QuoteCustomerForm;
